import { Song, SongTrack } from '~/interfaces';
import getSongBeatLength from '~/modules/songs/utils/get-song-beat-length';
import isNotesSection from '~/modules/songs/utils/is-notes-section';
import { NoteIndexEntry } from '~/modules/songs/utils/note-index';

/**
 * What one track demands, measured from its notes. Shared with `scripts/generate-note-index.ts`, so
 * a difficulty worked out from a song held in memory and one read from the index mean the same
 * thing rather than merely resembling each other.
 *
 * Every field survives the processing a song goes through on the way to being played -
 * `add-headstart` and the normalisers move where notes start, and none of them changes how many
 * there are, how long they are or how far the line reaches.
 */
export const measureTrack = (song: Song, track: SongTrack | undefined): NoteIndexEntry | null => {
  if (!track) return null;

  const notes = track.sections.filter(isNotesSection).flatMap((section) => section.notes);
  if (notes.length === 0) return null;

  const pitches = notes.map((note) => note.pitch);
  const beats = notes.reduce((total, note) => total + note.length, 0);

  return {
    notes: notes.length,
    // Semitones between the lowest and highest note. The game scores by pitch class, so where the
    // line sits does not matter - how far it reaches does.
    span: Math.max(...pitches) - Math.min(...pitches),
    secondsSung: Math.round((beats * getSongBeatLength(song)) / 1000),
    // In beats, so it stays comparable between songs of different tempos. Two decimals is well past
    // what any of this is used for, and full floats would double the index file.
    averageNoteLength: Math.round((beats / notes.length) * 100) / 100,
  };
};
